import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"
import FileUpload from "customerAttachments/components/AttachmentDetail/ImageCropUpload"
import PreviewPDF from "customerAttachments/components/AttachmentDetail/PreviewPDF"
import BreadCrumb from "customerAttachments/components/BreadCrumb"
import Button from "customerAttachments/components/Button"
import Loading from "customerAttachments/components/Loading"
import { IdCards } from "customerAttachments/constants"
import { useModal } from "customerAttachments/contexts/ModalProvider"
import useWindowDimensions, { DeviceType } from "customerAttachments/hooks/useWIndowDimensions"
import { toBase64 } from "customerAttachments/utils/fileUtil"

import styles from "./styles.module.scss"

type FileData = {
  FileName: string
  Base64: string
}

type AttachmentDetailProps = {
  attachTypeText: string
  goBack: () => void
  url: string
  isLoading: boolean
  onSubmit: (file: FileData[]) => void
  attachType: string
  setFiles: React.Dispatch<React.SetStateAction<(FileData | null)[]>>
  files: (FileData | null)[]
  isUploading: boolean
}
const MAX_PDF_SIZE = 10 // 10MB

const AttachmentDetail = ({
  attachTypeText,
  attachType,
  goBack,
  url = "",
  isLoading,
  isUploading,
  onSubmit,
  files,
  setFiles,
}: AttachmentDetailProps) => {
  const { openModal } = useModal()
  const { t } = useTranslation(["sales"])
  const [searchParams] = useSearchParams()
  const actionStatus = searchParams.get("action")
  const isIdCard = attachType && IdCards.includes(attachType)
  const { height, deviceType } = useWindowDimensions()
  const isMobile = deviceType === DeviceType.MOBILE

  const handleSelectPDF = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isUploading) return
    const file = event.target.files?.[0]
    event.target.value = ""

    if (file) {
      if (file.type !== "application/pdf") {
        openModal({
          title: t("customer_attachments.select_pdf"),
          content: t("upload.text_please_check_file"),
          showCancel: false,
        })
        return
      }

      if (file.size > MAX_PDF_SIZE * 1024 * 1024) {
        openModal({
          title: t("upload.title_file_too_large"),
          content: t("upload.text_file_too_large", { limit: MAX_PDF_SIZE }),
          showCancel: false,
        })
        return
      }

      const base64File = await toBase64(file)
      openModal({
        title: t("customer_attachments.select_pdf"),
        content: t("customer_attachments.selected_file", { fileName: file.name }),
        showCancel: false,
        confirmButtonText: t("order.checklist_confirm_btn_confirm"),
        onConfirm: () => {
          return onSubmit([
            {
              FileName: file.name,
              Base64: base64File,
            },
          ])
        },
      })
    }
  }

  const handleChangeFile = (file: FileData, index: number) => {
    setFiles((prev) => {
      const updatedFiles = [...prev]
      updatedFiles[index] = file
      return updatedFiles
    })
  }

  const fileUploadComponents = Array.from({ length: isIdCard ? 2 : 1 }, (_, index) => {
    const isFront = index === 0
    const id = `${attachType}${isFront ? "Front" : "Back"}`
    const text = isIdCard ? (index === 0 ? t("mobile.id_front") : t("mobile.id_back")) : attachTypeText || ""
    return (
      <FileUpload
        key={index}
        id={id}
        text={text}
        onChange={(file) => {
          handleChangeFile(file, index)
        }}
      />
    )
  })

  const enableSubmit = files.every((file) => file !== null)

  const renderContent = () => {
    return actionStatus === "preview" ? (
      <PreviewPDF url={url} />
    ) : (
      <div className={styles.editContainer}>
        <p className={styles.editContainer_description}>
          {t("customer_attachments.click_to_capture")}
          <label htmlFor="pdfFile">{t("customer_attachments.select_pdf")}</label>
        </p>
        <input
          type="file"
          id="pdfFile"
          accept="application/pdf"
          className={styles.fileInput}
          style={{ display: "none" }}
          onChange={handleSelectPDF}
        />
        {fileUploadComponents}
        <Button
          disabled={!enableSubmit}
          loading={enableSubmit && isUploading}
          onClick={() => {
            onSubmit(files.filter((file): file is FileData => file !== null))
          }}>
          {t("order.checklist_confirm_btn_confirm")}
        </Button>
      </div>
    )
  }

  return (
    <>
      <BreadCrumb text={attachTypeText} onClick={goBack} />
      <div className={styles.container} style={{ height: `calc(${height}px - ${isMobile ? 103 : 127}px)` }}>
        <Loading loading={isLoading}>{renderContent()}</Loading>
      </div>
    </>
  )
}

export default AttachmentDetail
